import * as logger from "./logger";
import { getServerDate } from "./storage/date";
import dayjs from "./dayjs";
import { sanitizeUserName } from "./users";
import { handleJS } from "./error";
import constants from "../constants";
import { dispatcher, DispatcherEvents } from "./dispatcher";
import { ITaskOptions } from "../modules/serviceBase";

export const TASK_NAME_DIVIDER = "| |"; // spaces are not allowed in user name
const DATE_OBJ_CHANGE_THRESHOLD = 1000 * 60 * 2;
const LOGGING_TIME = 1800;
const CLOCK_DRIFT_TASK = "@clockDrift";
export const UPLOAD_EVENT_LOGS_TASK = "@uploadEventLogs";

interface ITaskNameDictionary<T> {
    [ name: string ]: T;
}

interface ISchedule {
    base: string;
    user: string;
    run: (...args: any[]) => void | Promise<number>;
    interval: number;
    startingInterval: number; // The interval to be used when scheduling nextRun for the first time. If runLater = true, will be added to the interval after the first run of the task.
    enabled: boolean;
    nextRun?: Date | null;
    onDemand?: boolean; // On demand only runs once when enabled, then is disabled until next run
}

export interface IScheduler {
    registerTask(
        name: string,
        startFunc: (...args: any[]) => void,
        msInterval: number,
        userName?: string,
        runLater?: boolean,
        registerOnce?: boolean,
        onDemand?: boolean,
        startingInterval?: number,
        delayByInterval?: boolean): Scheduler;
    registerTaskWithOptions(task: (...args: any[]) => void, taskOptions: ITaskOptions, userName?: string): Scheduler;
    rescheduleTask(taskToReschedule: string, intervalOverrideMs?: number, username?: string);
    rescheduleAllTasks(): void;
    removeClockDriftTask(taskName: string): void;
    unregisterTask(name: string, userName?: string): Scheduler;
    unregisterAll(userName: string): void;
    isTaskRegistered(name: string, userName?: string): boolean;
    hasClockDriftTask(): boolean;
    start(tasks: string[] | string, userName?: string): Scheduler;
    startForUser(userName: string): void;
    stop(tasks: string[] | string, userName?: string): Scheduler;
    stopForUser(userName: string): void;
    fire(tasks: string[] | string, userName?: string): void;
    fireAllWithMatchingKey(task: string): void;
    setInterval(tasks: string[] | string, msInterval: number, userName?: string): Scheduler;
    updateTask(tasks: string[] | string, userName: string | undefined, callback: (task: ISchedule) => void): Scheduler;
    getNextRunDate(name: string, userName?: string): Date | null;
    getSplitKey(key: string): string[] | [string | undefined, string];
    getInterval(task: string, username?: string): number;
    getScheduledTaskKeys(): string[];
    getActiveTaskNamesForUsername(userName: string): string[];
}

export class Scheduler implements IScheduler {
    private readonly schedules: ITaskNameDictionary<ISchedule>;
    private needsNewDrift = false;
    private clockDriftTask: ISchedule | null;
    private clockDriftTaskInProgress = false;
    private previousTime: Date;
    private loggingCounter = 1; // Logging. Start at 1 initially as the interval waits 1 sec to start
    private logStartTime: number; // Logging. Want to know how much time passed in the local environment, don't want clock drift values affecting this

    constructor() {
        this.schedules = {};
        this.logStartTime = new Date().getTime();
        setInterval(() => this.run(), 1000);
    }

    getSplitKey(key: string): string[] | [string | undefined, string] {
        if (key.indexOf(TASK_NAME_DIVIDER) === -1) {
            return [undefined, key];
        }
        return key.split(TASK_NAME_DIVIDER);
    }

    registerTask(
        name: string,
        startFunc: (...args: any[]) => void,
        msInterval: number,
        userName?: string,
        runLater = false,
        registerOnce = false,
        onDemand = false,
        startingInterval = 0,
        delayByInterval = false
    ): Scheduler {
        if (registerOnce) {
            let skipRegisteringTask = false;
            // Check if the task was already registered for any user
            for (let key in this.schedules) {
                if (key.indexOf(name) !== -1) {
                    skipRegisteringTask = true;
                    break;
                }
            }
            if (skipRegisteringTask) {
                return this; // Task was already registered for a user and we don't want to register it again
            }
        }

        let interval = msInterval || 1;
        let scheduleName = this.getScheduleKey(name, userName);

        let task: ISchedule = {
            base: name,
            user: sanitizeUserName(userName || ""),
            run: startFunc || constants.NOOP,
            startingInterval,
            interval,
            enabled: false,
            onDemand
        };
        if (runLater) {
            task.nextRun = this.calculateNextRun(task, startingInterval + (delayByInterval ? interval : 0));
        }

        this.schedules[scheduleName] = task;
        logger.log(
            logger.Source.SERVICES,
            `Registered task '${ name }'. Next run: ${ dayjs(task.nextRun || new Date(), undefined, "en").format("llll") } runlater=${ runLater } delayByInterval=${ delayByInterval }`,
            userName
        );

        // We store this task since we need it for this.checkTime()
        if (this.isClockDriftTask(name)) {
            this.clockDriftTask = this.schedules[scheduleName];
        }

        return this;
    }

    registerTaskWithOptions(task: (...args: any[]) => void, taskOptions: ITaskOptions, userName?: string): Scheduler {
        return this.registerTask(
            taskOptions.name,
            task,
            taskOptions.msInterval,
            userName,
            taskOptions.hasDelayAfterStart,
            taskOptions.registerOnceForAllUsers,
            taskOptions.onDemand,
            taskOptions.startingInterval,
            taskOptions.delayByInterval
        );

    }

    rescheduleTask(taskToReschedule: string, intervalOverrideMs?: number, username?: string) {
        let schedules = this.schedules;
        for (let taskName in schedules) {
            let task = schedules[taskName];
            let splitKey = this.getSplitKey(taskName);
            if (splitKey[1] === taskToReschedule) {
                if (username && splitKey[0] !== username) {
                    continue;
                }
                delete task.nextRun;
                task.nextRun = this.calculateNextRun(task, intervalOverrideMs);
                logger.log(
                    logger.Source.SERVICES,
                    `Task '${ splitKey[1] }' rescheduled. Next run: ${ dayjs(task.nextRun, undefined, "en").format("llll") }`,
                    splitKey[0]
                );
            }
        }
    }

    rescheduleAllTasks() {
        let schedules = this.schedules;
        let now = getServerDate();
        dispatcher.trigger(DispatcherEvents.ClockDrift);
        for (let taskName in schedules) {
            if (schedules.hasOwnProperty(taskName)) {
                let task = schedules[taskName];
                let splitKey = this.getSplitKey(taskName);

                if (task.nextRun && task.nextRun.getTime() > task.interval + task.startingInterval + now.getTime()) {
                    delete task.nextRun;
                    task.nextRun = this.calculateNextRun(task);
                    logger.log(
                        logger.Source.SERVICES,
                        `Task '${ splitKey[1] }' rescheduled. Next run: ${ dayjs(task.nextRun, undefined, "en").format("llll") }`,
                        splitKey[0]
                    );
                }
            }
        }
    }

    removeClockDriftTask(taskName: string) {
        if (this.clockDriftTask && this.isClockDriftTask(taskName)) {
            this.clockDriftTask = null;
        }
    }

    unregisterTask(name: string, userName?: string) {
        let scheduleName = this.getScheduleKey(name, userName);

        if (this.schedules.hasOwnProperty(scheduleName)) {
            this.removeClockDriftTask(this.schedules[scheduleName].base);
            delete this.schedules[scheduleName];
            logger.log(logger.Source.SERVICES, `Unregistered task '${ name }'`, userName);
        }

        return this;
    }

    unregisterAll(userName: string) {
        let taskKeysForDeletion: any[] = [];
        for (let taskName in this.schedules) {
            if (!userName || (this.schedules[taskName].user && sanitizeUserName(this.schedules[taskName].user) === sanitizeUserName(userName))) {
                taskKeysForDeletion.push(taskName);
                this.removeClockDriftTask(this.schedules[taskName].base);
            }
        }
        taskKeysForDeletion.forEach(key => {
            delete this.schedules[key];
        });
        logger.log(logger.Source.SERVICES, "Unregistered all tasks", userName);
    }

    isTaskRegistered(name: string, userName?: string) {
        let taskName = this.getScheduleKey(name, userName);
        return this.schedules.hasOwnProperty(taskName);
    }

    // Also used for UTs
    hasClockDriftTask() {
        if (this.clockDriftTask) {
            return true;
        }

        let taskName: string;
        let taskNames = Object.keys(this.schedules) || [];
        for (let i = 0; i < taskNames.length; i++) {
            taskName = taskNames[i];
            if (this.isClockDriftTask(this.schedules[taskName].base)) {
                this.clockDriftTask = this.schedules[taskName];
                return true;
            }
        }

        return false;
    }

    start(tasks: string[] | string, userName?: string) {
        return this.updateTask(tasks, userName, task => {
            task.enabled = true;
        });
    }

    startForUser(userName: string) {
        const sanitizedUserName = sanitizeUserName(userName);
        for (let taskName in this.schedules) {
            let task = this.schedules[taskName];
            if (!task.onDemand && task.user && sanitizeUserName(task.user) === sanitizedUserName) {
                task.enabled = true;
            }
        }
    }

    stop(tasks: string[] | string, userName?: string) {
        return this.updateTask(tasks, userName, task => {
            task.enabled = false;
        });
    }

    stopForUser(userName: string) {
        for (let taskName in this.schedules) {
            let task = this.schedules[taskName];
            if (task.user && sanitizeUserName(task.user) === sanitizeUserName(userName)) {
                task.enabled = false;
            }
        }
    }

    fire(tasks: string[] | string, userName?: string) {
        this.updateTask(tasks, userName, task => {
            delete task.nextRun;
        });
    }

    fireAllWithMatchingKey(task: string) {
        let schedules = this.schedules;
        for (let taskName in schedules) {
            let nameMatch = taskName.split(TASK_NAME_DIVIDER);
            if (nameMatch[1] && nameMatch[1].toLowerCase() === task.toLowerCase()) {
                delete schedules[taskName].nextRun;
            }
        }
    }

    setInterval(tasks: string[] | string, msInterval: number, userName?: string) {
        return this.updateTask(tasks, userName, task => {
            task.interval = msInterval || 1;
        });
    }

    updateTask(tasks: string[] | string, userName: string | undefined, callback: (task: ISchedule) => void) {
        ([] as string[]).concat(tasks).forEach(taskName => {
            let task = this.schedules[this.getScheduleKey(taskName, userName)];
            if (task) {
                callback(task);
            }
        });
        return this;
    }

    getNextRunDate(name: string, userName?: string) {
        let key = this.getScheduleKey(name, userName);
        let task = this.schedules[key];

        return !!task && task.nextRun || null;
    }

    // Methods below are for unit testing
    getInterval(task: string, username?: string) {
        return this.schedules[this.getScheduleKey(task, username)].interval;
    }

    getScheduledTaskKeys() {
        return Object.keys(this.schedules);
    }

    getActiveTaskNamesForUsername(userName: string) {
        let tasksForUser: string[] = [];
        for (let taskName in this.schedules) {
            let task = this.schedules[taskName];
            if (task.user && sanitizeUserName(task.user) === sanitizeUserName(userName) && task.enabled) {
                tasksForUser.push(taskName);
            }
        }
        return tasksForUser;
    }

    private isClockDriftTask(taskName: string) {
        return taskName === CLOCK_DRIFT_TASK;
    }

    private getScheduleKey(name: string, userName?: string) {
        if (!userName) {
            return name;
        }
        return `${ sanitizeUserName(userName) }${ TASK_NAME_DIVIDER }${ name }`;
    }

    private calculateNextRun(task: ISchedule, intervalOverride?: number) {
        let interval = intervalOverride || task.interval + (!task.nextRun ? task.startingInterval : 0);
        return new Date(Math.floor((getServerDate().getTime() + interval) / 1000) * 1000);
    }

    private checkDateTime() {
        if (this.clockDriftTaskInProgress) {
            return;
        }

        if (!this.hasClockDriftTask()) {
            return;
        }

        let currentDate = new Date();
        if (this.needsNewDrift || (this.previousTime && Math.abs(this.previousTime.getTime() - currentDate.getTime()) > DATE_OBJ_CHANGE_THRESHOLD)) {
            if (!this.needsNewDrift) {
                // Disable clock drift task while this method is calling clock drift
                (this.clockDriftTask as ISchedule).enabled = false;

                // Only log first occurence of event
                logger.event(logger.Event.CLOCKDRIFT, `Drift detected. The previous time was ${ this.previousTime.toISOString() }. `
                                                        + `The current time is now ${ currentDate.toISOString() }. `
                                                        + `The user's localized time is ${ currentDate.toTimeString() }.`);

                // Since the date changed, the logging would be wrong now, need to reset it
                this.loggingCounter = 0;
                this.logStartTime = new Date().getTime();
            }

            this.clockDriftTaskInProgress = true;
            ((this.clockDriftTask as ISchedule).run() as Promise<number>)
                .then(() => {
                    this.clockDriftTaskInProgress = false;
                    this.needsNewDrift = false;

                    this.previousTime = currentDate;

                    this.rescheduleAllTasks();
                    if (this.clockDriftTask) {
                        (this.clockDriftTask).enabled = true;
                    }
                    logger.log(logger.Source.SERVICES, `Drift corrected. The server date has been updated to ${ getServerDate().toISOString() }`);
                })
                .catch(() => {
                    this.clockDriftTaskInProgress = false;

                    // Reschedule on first failure only
                    !this.needsNewDrift && this.rescheduleAllTasks();
                    this.needsNewDrift = true;
                });
        } else {
            this.previousTime = currentDate;
        }
    }

    private logInterval() {
        if (this.loggingCounter < LOGGING_TIME) {
            this.loggingCounter++;
            return;
        }

        let timeElapsed = (new Date().getTime() - this.logStartTime) / 1000;
        if (Math.abs(timeElapsed - this.loggingCounter) > 1) {
            logger.event(logger.Event.CLOCKDRIFT, `run has completed ${ LOGGING_TIME } times. It took ${ timeElapsed.toFixed(2) } seconds to complete.`);
        }

        this.loggingCounter = 0;
        this.logStartTime = new Date().getTime();
    }

    private run() {
        let schedules = this.schedules;
        let now = getServerDate();

        this.checkDateTime();
        this.logInterval();

        for (let taskName in schedules) {
            if (schedules.hasOwnProperty(taskName)) {
                let task = schedules[taskName];
                let taskFailed = false;
                let splitKey = this.getSplitKey(taskName);

                if (task.enabled && (!task.nextRun || task.nextRun <= now)) {
                    let delayedMs = task.nextRun ? now.getTime() - task.nextRun.getTime() : 0;

                    try {
                        task.run(!!task.nextRun);
                    } catch (ex) {
                        taskFailed = true;
                        handleJS(ex.message, null, null, null, ex, "scheduler");
                        continue;
                    } finally {
                        if (task.onDemand) {
                            task.enabled = false;
                        }

                        task.nextRun = this.calculateNextRun(task);
                        let result = taskFailed ? "failed" : "completed";
                        let nextRun = dayjs(task.nextRun, undefined, "en").format("llll");
                        let delayedBy = (delayedMs / 1000).toFixed(2);
                        let loggerMessage = `Task '${ splitKey[1] }' ${ result }. Next run: ${ nextRun }. Delayed by ${ delayedBy }s`;
                        if (taskFailed) {
                            logger.error(logger.Source.SERVICES, loggerMessage, splitKey[0]);
                        } else {
                            logger.log(logger.Source.SERVICES, loggerMessage, splitKey[0]);
                        }
                    }
                }
            }
        }
    }
}

let mainScheduler = new Scheduler();
export default mainScheduler;